/**
 * 다른 제품에서 넘어온 토큰으로 화면을 로그인 상태로 시작한다 — 주소 조각 `#sso=<토큰>` (72차, 2026-09-17).
 *
 * 이 길 자체는 docuai2 연계로 이미 쓰고 있었다. 그런데 토큰만 저장하고 **누구인지는 저장하지 않아서**,
 * loginHS 에 한 번도 로그인한 적 없는 브라우저로 넘어오면 토큰은 있는데 화면은 로그아웃으로 보였다
 * (App 이 `token`·`groupCode`·`userId` 세 개가 다 있어야 로그인으로 친다). LCS V2 가입자는 대부분
 * 그런 브라우저로 처음 넘어오므로 여기서 토큰 안의 신원까지 함께 적어 둔다.
 *
 * 서명 검증은 하지 않는다 — 할 수도 없고(비밀은 서버에만 있다) 할 필요도 없다. 화면에 이름을 띄우는 용도일 뿐,
 * 권한은 매 요청마다 서버가 토큰과 세션으로 다시 판단한다. 이 파일이 판단하는 것은 아무것도 없다.
 *
 * 조각(#)을 쓰는 이유: 질의문자열과 달리 서버로 전송되지 않아 접속 기록·리퍼러에 토큰이 남지 않는다.
 */

/** JWT 가운데 토막(payload)을 객체로. 형식이 아니면 null — 던지지 않는다. */
export function decodeJwtPayload(token) {
  try {
    const part = String(token || "").split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const bin = window.atob(padded);
    // atob 은 바이트열을 준다 — 한글 이름을 살리려면 UTF-8 로 다시 읽어야 한다.
    const json = decodeURIComponent(
      Array.from(bin)
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join(""),
    );
    const payload = JSON.parse(json);
    return payload && typeof payload === "object" ? payload : null;
  } catch (_e) {
    return null;
  }
}

/**
 * 토큰과 그 안의 신원을 저장한다. App 이 마운트할 때 바로 로그인으로 인식하도록 render 전에 부른다.
 * @returns {boolean} 신원까지 적었으면 true
 */
export function applySsoToken(token, { setToken, storage = window.localStorage } = {}) {
  if (!token) return false;
  if (typeof setToken === "function") setToken(token, "local");
  const payload = decodeJwtPayload(token);
  const userId = payload && (payload.sub || payload.userId);
  const groupCode = payload && (payload.groupId || payload.groupCode);
  if (!userId || !groupCode) return false;
  try {
    storage.setItem("groupCode", String(groupCode));
    storage.setItem("userId", String(userId));
    if (payload.name) storage.setItem("userName", String(payload.name));
  } catch (_e) {
    return false; // 사생활 보호 모드 등 — 토큰은 이미 저장했으니 흐름은 계속된다
  }
  return true;
}

/**
 * 주소 조각에서 `#sso=` 를 꺼내 적용하고 조각을 지운다(히스토리에 토큰을 남기지 않는다).
 * @returns {boolean} 토큰을 받았으면 true
 */
export function consumeSsoFragment({ setToken, location = window.location, history = window.history } = {}) {
  try {
    const m = (location.hash || "").match(/[#&]sso=([^&]+)/);
    if (!m || !m[1]) return false;
    applySsoToken(decodeURIComponent(m[1]), { setToken });
    history.replaceState(null, "", location.pathname + location.search);
    return true;
  } catch (_e) {
    return false; // 실패는 일반 로그인 흐름으로
  }
}
